import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import { safeSessionStorage } from "@/lib/storage";

/* ---------------------------------------------------------------------------
 * State shared between the new-session skeleton (SessionSkeleton.tsx) and the
 * real NewSessionView:
 *
 * - a handoff for text typed into the skeleton's live composer while the app
 *   is still loading, consumed by the real composer once it has mounted.
 *   Enter in the skeleton cannot send, so it marks the handoff as a pending
 *   send that the real composer performs once it is ready.
 *
 * The skeleton and the view it resolves into live in the same tab, so the
 * handoff is tab-scoped: another tab loading its own new session must not
 * adopt (let alone send) this one's text.
 * -------------------------------------------------------------------------*/

const COMPOSER_HANDOFF_KEY = "codesmith-skeleton-composer-handoff";
// Long enough to span a slow cold load; short enough that text abandoned by
// a navigation away mid-load doesn't resurface on a much later visit.
const COMPOSER_HANDOFF_TTL_MS = 5 * 60 * 1000;

export interface SkeletonComposerHandoff {
  /** `""` when the user emptied the box. */
  text: string;
  submitRequested: boolean;
  /** Org segment of the URL the text was typed under; a send stays in it. */
  org: string;
}

function readComposerHandoff(): SkeletonComposerHandoff | null {
  const raw = safeSessionStorage.get(COMPOSER_HANDOFF_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      text?: string;
      submitRequested?: boolean;
      org?: string;
      timestamp?: number;
    };
    if (
      typeof parsed.text !== "string" ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > COMPOSER_HANDOFF_TTL_MS
    ) {
      return null;
    }
    return {
      text: parsed.text,
      submitRequested: parsed.submitRequested === true,
      org: parsed.org ?? "",
    };
  } catch {
    return null;
  }
}

// Matches `useOrgFromPathname`: `/enterprise/ghe/acme/sessions/new` → `acme`.
function currentOrg(): string {
  const { pathname } = splitInstanceBasePath(window.location.pathname);
  return pathname.split("/")[1] ?? "";
}

function writeComposerHandoff(text: string, submitRequested: boolean) {
  safeSessionStorage.set(
    COMPOSER_HANDOFF_KEY,
    JSON.stringify({
      text,
      submitRequested,
      org: currentOrg(),
      timestamp: Date.now(),
    }),
  );
}

/**
 * Persist text typed into the skeleton composer while the app loads. Emptied
 * text is kept as `""` so the real composer knows the user cleared it and
 * neither shows nor auto-sends a `?prompt=` prefill instead. An edit
 * withdraws a pending send: the user is still composing, and Enter re-arms it.
 */
export function saveSkeletonComposerText(text: string) {
  writeComposerHandoff(text, false);
}

/** Enter in the skeleton: hand off the text with a send the real composer owes. */
export function requestSkeletonComposerSubmit(text: string) {
  writeComposerHandoff(text, true);
}

/**
 * Current handoff text, left in place. Safe to call during render; see
 * NewSessionView for why render must not consume it.
 */
export function peekSkeletonComposerText(): string | null {
  return readComposerHandoff()?.text ?? null;
}

/**
 * Consume the handoff. Called by NewSessionView once the real composer has
 * mounted, when the skeleton can no longer receive keystrokes.
 */
export function takeSkeletonComposerHandoff(): SkeletonComposerHandoff | null {
  const handoff = readComposerHandoff();
  safeSessionStorage.remove(COMPOSER_HANDOFF_KEY);
  return handoff;
}
